import store from '@/store'
import router from '@/router'
import { i18n } from '@/i18n'
import { isLikelyNetworkError } from './network-error'
import { buildLoginRedirectLocation } from '@/lib/helpers/deeplink'

let isLoggingOut = false

/**
 * Runs when the axios instance tries
 * to make an HTTP request.
 */
export const onRequest = (config) => {
    // We need to append the authentication
    // token to the request in case the
    // user is authenticated.
    if (store.getters['auth/loggedIn']) {
        config.headers.Authorization = `Bearer ${store.state.auth.token}`
    }
    // Language prefix of the active locale ('fr-FR' → 'fr'); robust to locales
    // without a region ('fr') where indexOf('-') would yield an empty header.
    config.headers['X-localization'] = (i18n.global.locale.value || 'en').split('-')[0]
    // We also need to indicate to the application
    // that we are performing an HTTP request.
    store.commit('other/request', true)
    return config
}

/**
 * Runs when the axios instance fails
 * to perform an HTTP request.
 */
export const onRequestError = (error) => {
    store.commit('other/request', false)
    return Promise.reject(error)
}

/**
 * Runs when the axios instance finishes
 * performing an HTTP request and have
 * the response ready.
 */
export const onResponse = (response) => {
    store.commit('other/request', false)
    return response
}

/**
 * Runs when the axios instance fails with
 * the HTTP response.
 */
export const onResponseError = (error) => {
    store.commit('other/request', false)
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const data = error.response?.data
        if (data) {
            // The given data was invalid.
            if (data.errors) {
                error.message = Object.values(data?.errors).flat().join('<br />')
            } else if (data.message) {
                error.message = data.message
            }
        }
        // Handle 401 Unauthorized - logout and redirect instead of reload
        if (error.response.status === 401 && error.config.url !== '/user' && !isLoggingOut) {
            const isGuest = router.currentRoute.value?.meta?.guest

            if (store.getters['auth/loggedIn']) {
                isLoggingOut = true
                store.dispatch('auth/logout')
                    .then(() => {
                        router.push(buildLoginRedirectLocation(router.currentRoute.value))
                    })
                    .catch(() => {})
                    .finally(() => {
                        isLoggingOut = false
                    })
            } else if (!isGuest) {
                // Not logged in and not on a guest page, redirect to login
                router.push(buildLoginRedirectLocation(router.currentRoute.value))
            }
            // If already on login page (isGuest), do nothing
        }
    } else {
        if (isLikelyNetworkError(error)) {
            error.isOffline = true
        }
    }
    return Promise.reject(error)
}
